import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface HiddenPhotoDTO {
  photoId: string;
  thumbnailUrl: string | null;
  originalName: string | null;
  sceneName: string | null;
  markedAt: string | null;
}

// Delete rides the shared photo-delete workflow envelope (locked list member /
// cover warning) — the panel only ever deletes one photo at a time.
export type HiddenPhotoDeleteResult =
  | { ok: true }
  | { ok: false; reason: 'locked' | 'cover_warning' | 'not_found' | 'no_access' };

interface HiddenPhotosState {
  galleryId: string | null;
  rows: HiddenPhotoDTO[];
  status: 'idle' | 'loading' | 'ready' | 'error';
}

@Injectable({ providedIn: 'root' })
export class GalleryHiddenPhotosApiService {
  private readonly http = inject(HttpClient);

  // Один владелец списка скрытых фото: и вкладка «Скрытые» в шапке, и сама панель
  // читают его отсюда. Иначе вкладка зависит от того, какая страница открыта, —
  // по прямой ссылке на /hidden-photos её просто не было (s1-ac3.3).
  private readonly state = signal<HiddenPhotosState>({ galleryId: null, rows: [], status: 'idle' });
  private inFlight: { galleryId: string; done: Promise<void> } | null = null;

  readonly photos = computed(() => this.state().rows);
  readonly count = computed(() => this.state().rows.length);
  readonly loaded = computed(() => this.state().status === 'ready');
  readonly failed = computed(() => this.state().status === 'error');

  load(galleryId: string): Promise<void> {
    // Шапка и панель просят список одновременно при заходе по прямой ссылке — один запрос на двоих.
    if (this.inFlight?.galleryId === galleryId) return this.inFlight.done;
    const done = this.fetch(galleryId);
    this.inFlight = { galleryId, done };
    return done;
  }

  async unhide(galleryId: string, photoId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`/api/gallery-photos/${galleryId}/unhide`, { photoId }));
      this.drop(galleryId, [photoId]);
      return true;
    } catch {
      return false;
    }
  }

  // Chunked to the server's batch DTO cap (1000 ids per request) — the hidden
  // list endpoint returns ALL rows, so one giant gallery must not turn
  // «Показать все гостям» into a guaranteed 400. A chunk that fails after an
  // earlier one succeeded still drops the rows already published and reports
  // that count, so the tab never keeps photos the server has let go.
  async unhideAll(galleryId: string, photoIds: string[]): Promise<{ unhidden: number; failed: boolean }> {
    const BATCH_MAX = 1000;
    const published: string[] = [];
    let unhidden = 0;
    try {
      for (let i = 0; i < photoIds.length; i += BATCH_MAX) {
        const chunk = photoIds.slice(i, i + BATCH_MAX);
        const result = await firstValueFrom(
          this.http.post<{ unhidden: number }>(`/api/gallery-photos/${galleryId}/unhide-batch`, { photoIds: chunk }),
        );
        unhidden += result.unhidden;
        published.push(...chunk);
      }

      this.drop(galleryId, photoIds);
      return { unhidden, failed: false };
    } catch {
      this.drop(galleryId, published);
      return { unhidden, failed: true };
    }
  }

  async deletePhoto(galleryId: string, photoId: string, confirmCover: boolean): Promise<HiddenPhotoDeleteResult> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ ok: boolean; reason?: string }>(`/api/gallery-photos/${galleryId}/delete`, {
          photoIds: [photoId],
          confirmCover,
        }),
      );
      if (result.ok) {
        this.drop(galleryId, [photoId]);
        return { ok: true };
      }

      const reason = result.reason === 'locked' || result.reason === 'cover_warning' ? result.reason : 'not_found';
      return { ok: false, reason };
    } catch {
      return { ok: false, reason: 'no_access' };
    }
  }

  private async fetch(galleryId: string): Promise<void> {
    this.state.set({ galleryId, rows: [], status: 'loading' });
    try {
      const rows = await firstValueFrom(this.http.get<HiddenPhotoDTO[]>(`/api/gallery-photos/${galleryId}/client-private`));
      // RACE-SAFE: галерею успели сменить — списком владеет уже более новый запрос.
      if (this.state().galleryId === galleryId) this.state.set({ galleryId, rows, status: 'ready' });
    } catch {
      if (this.state().galleryId === galleryId) this.state.update(s => ({ ...s, status: 'error' }));
    } finally {
      if (this.inFlight?.galleryId === galleryId) this.inFlight = null;
    }
  }

  // Возврат гостям и удаление сразу снимают строку с общего списка — вкладка «Скрытые»
  // теряет единицу вместе с таблицей, без перезахода на страницу галереи.
  private drop(galleryId: string, photoIds: string[]): void {
    if (this.state().galleryId !== galleryId) return;
    const gone = new Set(photoIds);
    this.state.update(s => ({ ...s, rows: s.rows.filter(row => !gone.has(row.photoId)) }));
  }
}
